import { NgClass, NgPlural, NgPluralCase } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import { IAddress } from '@bytel/checkout';

import { CheckoutStore } from '@interfaces/store.interface';

import { SHIPPING_MODES } from '../../constants';
import { DataCyDirective } from '../../directives/data-cy.directive';
import { DataCsMaskDirective } from '../../directives/data-mask.directive';
import { GMapsConfig, GMapsMarker } from '../../models/bytel-gmaps.interface';
import { MAP_LOCATION, TAB_CHOICE } from '../../models/store-locator.interface';
import { GmapsComponent } from '../gmaps/gmaps.component';
import { StoreComponent } from './store/store.component';

declare let window;

@Component({
    selector: 'checkout-store-locator',
    templateUrl: './store-locator.component.html',
    styleUrl: 'store-locator.component.scss',
    standalone: true,
    imports: [NgClass, NgPlural, NgPluralCase, DataCsMaskDirective, DataCyDirective, StoreComponent, GmapsComponent],
})
export class StoreLocatorComponent implements OnInit, OnChanges, OnDestroy {
    public currentTab: TAB_CHOICE = TAB_CHOICE.STORES;
    public tabChoices = TAB_CHOICE;

    public isMobileMQ: boolean = false;
    public currentMarker: GMapsMarker;
    public mq: MediaQueryList;
    public markers: GMapsMarker[] = [];
    public displayStoreModal: boolean = true;
    public storeTitle: string = 'magasins';
    @Input() public shouldAvoidScrollDelay: boolean = false;
    @Input() public currentStore: CheckoutStore;

    @Input() public mapConfig: GMapsConfig;

    @Input() public mapLocation: MAP_LOCATION = MAP_LOCATION.LEFT;
    @Input() public stores: CheckoutStore[] = [];
    @Input() public isLoading: boolean;
    @Input() public address: IAddress;
    @Input() public inlineAddress: string;

    @Output() public selectedStore: EventEmitter<CheckoutStore> = new EventEmitter<null>();
    @Output() public editAddressEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

    public readonly CONTAINER_CLASS: string = 'stores-container';
    private readonly MOBILE_BREAKPOINT: string = '(max-width: 768px)';

    constructor(private ngZone: NgZone) {
        this._mediaQueryListListener = this._mediaQueryListListener.bind(this);
        this.mq = window.matchMedia(this.MOBILE_BREAKPOINT);
    }

    public ngOnInit(): void {
        this._initBreakpointMonitoring();
        this.markers = this._generateMarkers(this.stores);
        if (this.currentStore) {
            this.selectedStore.emit(this.currentStore);
            this.currentMarker = this.markers.find((marker) => marker.id === this.currentStore.id);
        }
        this.mapConfig = {
            ...this.mapConfig,
            ...{
                center: {
                    lat: this.markers[0].position.lat,
                    lng: this.markers[0].position.lng,
                },
            },
        };
    }

    public ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.mq.removeEventListener('change', this._mediaQueryListListener);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.stores) {
            this.markers = this._generateMarkers(this.stores);
        }
        if (changes.currentStore) {
            this.currentMarker = this.markers.find((marker) => marker.id === this.currentStore?.id);
        }

        this._setStoreTitle();
    }

    public handleSelectedStore(event: CheckoutStore): void {
        // Trigger store position update on the map
        this.currentMarker = this.markers.find((marker) => marker.id === event.id);
        this.currentStore = this.stores.find((store) => store.id === event.id);
        this.displayStoreModal = true;
        this.selectedStore.emit(this.currentStore);
    }

    public handleSelectedMarker(store: CheckoutStore): void {
        // Trigger scroll on selected store in stores list
        this.currentMarker = this.markers.find((marker) => marker.id === store.id);
        this.currentStore = this.stores.find((storeItem) => storeItem.id === store.id);
        this.selectedStore.emit(this.currentStore);
        this.displayStoreModal = true;
        this._scrollToStore(this.currentStore);
    }

    public changeTab(tab: TAB_CHOICE): void {
        this.currentTab = tab;
        this._focusOnCurrentStoreOnMobile();
    }

    public changeAddressEvent(evt: Event): void {
        if (evt && evt.preventDefault) {
            evt.preventDefault();
        }
        this.editAddressEvent.emit(true);
    }

    public hideModal(): void {
        this.displayStoreModal = false;
        this.currentMarker = null;
        this.currentStore = null;
        this.selectedStore.emit(this.currentStore);
    }

    private _setStoreTitle(): void {
        if (
            this.stores.some(
                (s) =>
                    s.deliveryType === SHIPPING_MODES.RELAIS_COLIS || s.deliveryType === SHIPPING_MODES.RELAIS_EXPRESS,
            )
        ) {
            this.storeTitle = this.stores?.length > 1 ? 'points de retrait' : 'point de retrait';
        } else {
            this.storeTitle = this.stores?.length > 1 ? 'magasins' : 'magasin';
        }
    }

    private _generateMarkers(stores: CheckoutStore[]): GMapsMarker[] {
        const markers: GMapsMarker[] = [];

        stores.forEach((store: CheckoutStore) => {
            markers.push({
                id: store.id,
                position: {
                    lat: store.coordinates.lat,
                    lng: store.coordinates.lng,
                },
                options: {
                    animation: google.maps.Animation.DROP,
                    icon: store.icons.DEFAULT,
                },
                icons: store.icons,
                active: false,
                size: new google.maps.Size(20, 32),
                origin: new google.maps.Size(0, 0),
            });
        });

        const homeMarker = {
            id: 'home-marker',
            position: {
                lat: this.address.coordonnees.lat,
                lng: this.address.coordonnees.lng,
            },
            options: {
                animation: google.maps.Animation.DROP,
                icon: '',
            },
            icons: {
                DEFAULT: {
                    url: `assets/checkout/marker-solid-home.svg`,
                    size: new google.maps.Size(32, 32),
                    scaledSize: new google.maps.Size(32, 32),
                    iconName: 'marker-solid-home',
                },
                SELECTED: {
                    url: `assets/checkout/marker-solid-home.svg`,
                    size: new google.maps.Size(32, 32),
                    scaledSize: new google.maps.Size(32, 32),
                    iconName: 'marker-solid-home',
                },
            },
            active: false,
            size: new google.maps.Size(20, 32),
            origin: new google.maps.Size(0, 0),
        };
        markers.push(homeMarker);
        return markers;
    }

    private _focusOnCurrentStoreOnMobile(): void {
        if (this.isMobileMQ && this.currentStore && this.currentTab === TAB_CHOICE.STORES) {
            this._scrollToStore(this.currentStore);
        }
    }

    private _initBreakpointMonitoring(): void {
        this.isMobileMQ = this.mq.matches;
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.mq.addEventListener('change', this._mediaQueryListListener, { passive: this.shouldAvoidScrollDelay });
    }

    private _mediaQueryListListener(evt: any): void {
        if (evt.matches !== this.isMobileMQ) {
            this.ngZone.run(() => {
                this.isMobileMQ = evt?.matches;
            });
        }
    }

    private _scrollToStore(store: CheckoutStore): void {
        const elmHeight: number = document.querySelector('.store').clientHeight;
        document.querySelector(`.${this.CONTAINER_CLASS}`).scrollTo({
            top: store.listIndex * elmHeight,
            behavior: 'smooth',
        });
    }
}
